/**
 * Safe storage utilities that gracefully handle when storage is not available.
 * Returns null/does nothing when storage access is denied or unavailable.
 */

interface SafeStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

function getStorage(type: 'local' | 'session'): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return type === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function createSafeStorage(type: 'local' | 'session'): SafeStorage {
  return {
    getItem: (key: string) => {
      const storage = getStorage(type);
      if (!storage) return null;
      try {
        return storage.getItem(key);
      } catch {
        return null;
      }
    },

    setItem: (key: string, value: string) => {
      const storage = getStorage(type);
      if (!storage) return;
      try {
        storage.setItem(key, value);
      } catch {}
    },

    removeItem: (key: string) => {
      const storage = getStorage(type);
      if (!storage) return;
      try {
        storage.removeItem(key);
      } catch {}
    },
  };
}

export const safeLocalStorage = createSafeStorage('local');

export const safeSessionStorage = createSafeStorage('session');

/**
 * Read JSON data from localStorage with a fallback value.
 * Returns fallback on any error (missing key, parse error, etc.).
 */
export function readJsonFromLocalStorage<T>({ key, fallback }: { key: string; fallback: T }): T {
  try {
    const stored = safeLocalStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Write JSON data to localStorage.
 * Silently fails on any error (storage full, serialization error, etc.).
 */
export function writeJsonToLocalStorage<T>({ key, value }: { key: string; value: T }): void {
  try {
    safeLocalStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

/**
 * Read JSON data from sessionStorage with a fallback value.
 * Returns fallback on any error (missing key, parse error, etc.).
 */
export function readJsonFromSessionStorage<T>({ key, fallback }: { key: string; fallback: T }): T {
  try {
    const stored = safeSessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Write JSON data to sessionStorage.
 * Silently fails on any error (storage full, serialization error, etc.).
 */
export function writeJsonToSessionStorage<T>({ key, value }: { key: string; value: T }): void {
  try {
    safeSessionStorage.setItem(key, JSON.stringify(value));
  } catch {}
}
